'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export type BoardKey = 'sales' | 'production' | 'billing';

// Shared registry of the pipeline boards the user can switch between.
export const BOARDS: { key: BoardKey; label: string; path: string; color: string }[] = [
  { key: 'sales',      label: 'Sales',      path: '/customer-dashboard', color: '#6366F1' },
  { key: 'production', label: 'Production', path: '/production-board',    color: '#3B82F6' },
  { key: 'billing',    label: 'Billing',    path: '/billing-board',       color: '#10B981' },
];

interface BoardSwitcherProps {
  current: BoardKey;
}

export default function BoardSwitcher({ current }: BoardSwitcherProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = BOARDS.find(b => b.key === current) ?? BOARDS[0];

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2.5 pl-3 pr-2.5 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: active.color }} />
        <span className="text-xl font-bold leading-none" style={{ color: '#1C2E5E' }}>{active.label}</span>
        <Icon name="ChevronDownIcon" size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1.5 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50"
          role="listbox"
        >
          <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">Switch board</p>
          {BOARDS.map(b => {
            const isActive = b.key === current;
            return (
              <button
                key={b.key}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setOpen(false);
                  if (!isActive) router.push(b.path);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                  isActive ? 'bg-gray-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: b.color }} />
                <span className="text-sm font-semibold flex-1" style={{ color: '#1C2E5E' }}>{b.label} Board</span>
                {isActive && <Icon name="CheckIcon" size={16} className="text-gray-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
